/**
 * "Accounts you commented on" — the per-account view of the range's comments (SPEC
 * `Content Performance` § Comments Performance).
 *
 * `/view`'s Per-member table on another dataset: same `.pm-table` markup, same `SortableHeader`,
 * same comparator shape (sorting reads `Explained.value`, a "—" sinks in both directions, name then
 * key break ties) — the decisions all live in `contentState.ts` so the node suite can reach them.
 *
 * The two × columns divide by two different medians on purpose, and each carries the server's own
 * tooltip naming its denominator: `I ×` compares this account with the other accounts in the table,
 * `Med ×` compares a comment here with every comment ever imported. Nothing is computed here — the
 * rows arrive as envelopes and the name is a plain link out, since an account you commented on is
 * not necessarily a profile the app tracks.
 */

import { useMemo } from 'react';
import type { ContentCommentAuthorRowDTO } from '@lw/shared';
import { ExternalLinkIcon, MultiplierBadge, Num } from '../../components';
import { SortableHeader } from '../home/SortableHeader';
import {
  sortContentAuthors,
  type ContentAuthorsSort,
  type ContentAuthorsSortKey,
} from './contentState';

const COLUMNS: readonly { key: ContentAuthorsSortKey; label: string; title: string }[] = [
  { key: 'comments', label: 'Comments', title: 'Comments you left on this account’s posts' },
  { key: 'impressions', label: 'Impressions', title: 'Σ impressions of those comments' },
  {
    key: 'impressionsX',
    label: 'I ×',
    title: 'Σ impressions ÷ the median Σ impressions of the accounts listed',
  },
  {
    key: 'medianImpressions',
    label: 'Median',
    title: 'Median impressions per comment on this account’s posts',
  },
  {
    key: 'medianImpressionsX',
    label: 'Med ×',
    title: 'That median ÷ the median impressions across all imported comments',
  },
];

export type ContentAuthorsTableProps = {
  rows: readonly ContentCommentAuthorRowDTO[];
  sort: ContentAuthorsSort;
  onSort: (key: ContentAuthorsSortKey) => void;
};

export function ContentAuthorsTable({ rows, sort, onSort }: ContentAuthorsTableProps) {
  const sorted = useMemo(() => sortContentAuthors(rows, sort), [rows, sort]);

  return (
    <div className="table-wrap">
      <table className="table pm-table">
        <thead>
          <tr>
            <SortableHeader
              label="Account"
              columnKey="name"
              sort={sort}
              onSort={onSort}
              title="The account whose posts you commented on"
            />
            {COLUMNS.map((column) => (
              <SortableHeader
                key={column.key}
                label={column.label}
                columnKey={column.key}
                sort={sort}
                onSort={onSort}
                title={column.title}
                className="num"
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.key}>
              <td>
                {row.url === null ? (
                  <span className="cell-ellipsis">{row.name}</span>
                ) : (
                  <a
                    className="cn-author"
                    href={row.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    title={row.name}
                  >
                    <span className="cell-ellipsis">{row.name}</span>
                    <ExternalLinkIcon size={12} />
                  </a>
                )}
              </td>
              <td className="num">
                <Num value={row.comments} withTooltip={false} />
              </td>
              <td className="num">
                <Num value={row.impressions} withTooltip={false} />
              </td>
              <td className="num">
                <MultiplierBadge value={row.impressionsX} />
              </td>
              <td className="num">
                <Num value={row.medianImpressions} withTooltip={false} />
              </td>
              <td className="num">
                <MultiplierBadge value={row.medianImpressionsX} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ContentAuthorsTable;
